/* eslint-disable @typescript-eslint/no-explicit-any */

import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import { History, Location } from 'history';
import {
  AddressType,
  CASE_DATE_TIME_FORMAT,
  CASE_STATUSES_RDN_MATCH,
  CaseOrderType,
  CaseStatusRDNMatch,
  ImageOutputData,
  INVOLUNTARY_ORDER_TYPES,
  SessionBranch,
  SessionUser,
  SubscriptionPlan,
  UNASSIGNED_BRANCH_NAME,
  VOLUNTARY_ORDER_TYPES,
} from '@rapid-recovery-agency-inc/insightt-shared';
import {
  RdnRepossession,
  User,
  ValidatedLocationFromAddressQuery,
  CaseAddress,
  CaseStatus,
  UnitPendingSortKey,
  createCaseAddressDAO,
} from '@rapid-recovery-agency-inc/insightt-graphql';

import {
  FORMAT_DATE_TIME,
  SEARCH_PARAM_KEYS,
  TOP_RECOVERY_AGENTS_CARD_NAME,
  TOP_SPOTTERS_CARD_NAME,
} from '../constants/app.constants';
import { getIsInAdminGroup } from '../utils/users.utils';
import involuntaryIcon from '../assets/images/in-volentory-icon.png.png';
import voluntaryIcon from '../assets/images/volentory-icon.png';
import currentMarkerIcon from '../assets/img/markers/current.png';
import { getParamsObject, getQueryString, getSearchParam } from './html';
// This module must never import `../redux/store` (directly or transitively) or `shared/apollo.ts`'s
// `client`: `shared/apollo.ts` and `modules/subscription/subscription-api.jsx` (imported by
// `redux/store.tsx` at its top level) both import this file, so either would close a cycle back
// into `redux/store.tsx`'s module evaluation (T037). `modules/auth/sign-out.ts` loads the full
// cleanup lazily for exactly this reason.
import { signOut } from '../modules/auth';
import { CarouselSlide } from '@rapid-recovery-agency-inc/sloth-ui-web';
import {
  ExtendedVehicleData,
  ResolveAddressByPriorityParams,
  ResolveAddressByPriorityResult,
  ResolveCoordinatesByPriorityParams,
  ResolveCoordinatesByPriorityResult,
} from './types';

type SearchParamKeys = typeof SEARCH_PARAM_KEYS;
export type TabName = keyof SearchParamKeys;

export const formattedColorName = (colorText?: string | null) => {
  if (!colorText) return null;
  const firstCharacterColor = colorText.charAt(0)?.toUpperCase();

  const colorFormatted = firstCharacterColor + colorText.toLowerCase().slice(1);

  return colorFormatted.toLowerCase();
};
/**
 * This function is meant to be used to check if the user's coordinates are null.
 * As part of the logic to see if a map modal should be shown.
 * @returns bool
 */
export const areCoordinatesNull = (caseData: any) => {
  if (caseData?.status === CASE_STATUSES_RDN_MATCH.repossessed && caseData?.repoLat) {
    return false;
  }

  if (caseData?.status !== CASE_STATUSES_RDN_MATCH.repossessed && caseData?.spottedLat) {
    return false;
  }

  return true;
};

export const areCoordinatesNullForRepos = (caseData?: any, showGivenAddress?: boolean) => {
  if (caseData?.status === CaseStatusRDNMatch.REPOSSESSED && caseData?.rdnRepossession !== null) {
    return false;
  }

  if (caseData?.status !== CaseStatusRDNMatch.REPOSSESSED && caseData?.spottedLat !== null) {
    return false;
  }

  if (showGivenAddress && caseData.caseAddresses?.length > 0) {
    return false;
  }

  return true;
};

export const areCoordinatesNullForReposV2 = <T>(caseData?: ExtendedVehicleData<T>, showGivenAddress?: boolean) => {
  if (!caseData) return true;

  const isRepossessed = caseData.status === CaseStatusRDNMatch.REPOSSESSED;

  if (isRepossessed && caseData.rdnRepossession != null) {
    return false;
  }

  const hasSpottedCoords =
    caseData.spottedVehicleReports?.[0]?.caseAddress?.latitude != null &&
    caseData.spottedVehicleReports?.[0]?.caseAddress?.longitude != null;

  if (!isRepossessed && hasSpottedCoords) {
    return false;
  }

  const givenAddress = caseData.caseAddresses?.[0];

  if (showGivenAddress && givenAddress) {
    const hasGivenCoords = givenAddress.latitude != null && givenAddress.longitude != null;

    return Boolean(!hasGivenCoords && givenAddress.geocodedAt);
  }

  return true;
};

/*
 * @deprecated Since 2026-08-14. Use `resolveAddressByPriority` instead.
 */
export const getAddressForMissedRepossessionsAndTotalAssignments = (caseData: {
  status?: string;
  spottedAddress?: string;
  rdnRepossession: Pick<RdnRepossession, 'address'> | null;
  repoAddress?: string;
  givenAddress?: string | null;
}): string | undefined => {
  if (caseData.givenAddress && !caseData.spottedAddress && caseData.status !== CaseStatusRDNMatch.REPOSSESSED) {
    return caseData.givenAddress;
  }

  if (caseData.status !== CaseStatusRDNMatch.REPOSSESSED) {
    return caseData.spottedAddress;
  }

  if (caseData.rdnRepossession && caseData.status === CaseStatusRDNMatch.REPOSSESSED) {
    return caseData.rdnRepossession.address;
  }

  if (caseData.repoAddress && caseData.status === CaseStatusRDNMatch.REPOSSESSED) {
    return caseData.repoAddress;
  }

  return undefined;
};

export const getAddress = (caseData?: any) => {
  return caseData.repoAddress;
};

export const getAddressRankingCards = (queryObject: Record<string, any>, caseData: any): string | undefined => {
  if (queryObject.title === TOP_RECOVERY_AGENTS_CARD_NAME) {
    return caseData?.repoAddress;
  }

  if (queryObject.title === TOP_SPOTTERS_CARD_NAME) {
    return caseData?.spottedAddress;
  }

  return undefined;
};

export const areCoordinatesNullRankingCards = (queryObject: Record<string, any>, caseData: any) => {
  if (queryObject.title === TOP_RECOVERY_AGENTS_CARD_NAME && caseData?.repoLat) {
    return false;
  }

  if (queryObject.title === TOP_SPOTTERS_CARD_NAME && caseData?.spottedLat) {
    return false;
  }

  return areCoordinatesNull(caseData);
};

export const getVoluntaryInvoluntaryIcon = (orderType: CaseOrderType) => {
  const isInvoluntary = INVOLUNTARY_ORDER_TYPES.includes(orderType);
  const isVoluntary = VOLUNTARY_ORDER_TYPES.includes(orderType);

  let iconInfo = {};

  if (isInvoluntary) {
    iconInfo = {
      url: involuntaryIcon,
      scaledSize: new google.maps.Size(36, 46),
    };
  } else if (isVoluntary) {
    iconInfo = {
      url: voluntaryIcon,
      scaledSize: new google.maps.Size(36, 46),
    };
  } else {
    iconInfo = {
      url: currentMarkerIcon,
      scaledSize: new google.maps.Size(36, 46),
    };
  }

  return iconInfo;
};

export const msleep = async (miliSeconds: number) => {
  await new Promise((resolve) => {
    setTimeout(resolve, miliSeconds);
  });
};

export const sleep = async (seconds: number) => {
  await msleep(seconds * 1000);
};

export const dateCalculation = (
  calendarDate: string,
  timezone: string,
  isYearly: boolean | undefined,
  timeFormat = FORMAT_DATE_TIME,
) => {
  const now = moment();
  const nowFormatted = now.format('YYYY-MM');
  const currentCalendarDate = moment(calendarDate).format('YYYY-MM');
  let startDate;
  let endDate;

  if (isYearly) {
    startDate = moment.tz(calendarDate, timezone).startOf('year').utc().format(timeFormat);
  } else {
    startDate = moment.tz(calendarDate, timezone).startOf('month').utc().format(timeFormat);
  }

  if (nowFormatted === currentCalendarDate) {
    if (timezone === '') {
      endDate = moment().format(timeFormat);
    } else {
      endDate = moment.tz(moment(), timezone).utc().format(timeFormat);
    }
  } else if (isYearly && now.year() !== moment(calendarDate).year()) {
    endDate = moment.tz(calendarDate, timezone).endOf('year').utc().format(timeFormat);
  } else {
    endDate = moment.tz(calendarDate, timezone).endOf('month').utc().format(timeFormat);
  }

  return { startDate, endDate };
};

export const dateCalculationV2 = (
  calendarDate: string,
  timezone: string,
  isYearly: boolean | undefined,
  timeFormat = FORMAT_DATE_TIME,
) => {
  const nowFormatted = DateTime.now().toFormat('yyyy-MM');
  const currentCalendarDate = DateTime.fromISO(calendarDate).toFormat('yyyy-MM');
  let startDate;
  let endDate;

  if (isYearly) {
    startDate = DateTime.fromISO(calendarDate, { zone: timezone || undefined })
      .startOf('year')
      .toUTC()
      .toFormat(timeFormat);
  } else {
    startDate = DateTime.fromISO(calendarDate, { zone: timezone || undefined })
      .startOf('month')
      .toUTC()
      .toFormat(timeFormat);
  }

  if (nowFormatted === currentCalendarDate) {
    if (timezone === '') {
      endDate = DateTime.now().toFormat(timeFormat);
    } else {
      endDate = DateTime.now().setZone(timezone).toUTC().toFormat(timeFormat);
    }
  } else if (isYearly && DateTime.now().year !== DateTime.fromISO(calendarDate).year) {
    endDate = DateTime.fromISO(calendarDate, { zone: timezone || undefined })
      .endOf('year')
      .toUTC()
      .toFormat(timeFormat);
  } else {
    endDate = DateTime.fromISO(calendarDate, { zone: timezone || undefined })
      .endOf('month')
      .toUTC()
      .toFormat(timeFormat);
  }

  return { startDate, endDate };
};

export const getDateForCalculation = (
  isYearlyDate: string | boolean,
  localCalendarDate: string,
  localYtdCalenderDate: Record<string, any>,
) => {
  if (isYearlyDate === 'true' || isYearlyDate === true) {
    return moment(localYtdCalenderDate.endDate._d).format('YYYY-MM-DD');
  }

  return localCalendarDate;
};

export const getDateForCalculationV2 = (
  isYearlyDate: string | boolean,
  localCalendarDate: string,
  localYtdCalenderDate: { startDate: DateTime; endDate: DateTime },
) => {
  if (isYearlyDate === 'true' || isYearlyDate === true) {
    return localYtdCalenderDate.endDate.toISO() || '';
  }

  return localCalendarDate;
};

export const addressHyphenFormat = (str1: string, str2: string) => {
  if (str1 && str2) {
    return `${str1} - ${str2}`;
  }

  if (str1 && !str2) {
    return str1;
  }

  if (!str1 && str2) {
    return str2;
  }

  return '';
};

export const getBranchObjectById = (branches: SessionBranch[], branchId: number) => {
  const branchObject = branches.find((branch) => branch.id === branchId);
  if (branchObject) {
    return branchObject;
  }

  return { name: 'Unknown', id: -1 };
};

export const getNewUrlForTabOverviewCards = (location: Location<unknown>, tabName: TabName, tabValue: any) => {
  const searchParams = new URLSearchParams(location.search);
  searchParams.set(SEARCH_PARAM_KEYS[tabName], tabValue);

  const newUrl = `${location.pathname}?${searchParams.toString()}`;
  return newUrl;
};

export const navigateToNewUrlForTabs = (history: History<unknown>, tabName: TabName, tabValue: any) => {
  const { location } = history;
  const searchParams = new URLSearchParams(location.search);
  searchParams.set(SEARCH_PARAM_KEYS[tabName], tabValue);
  const newUrl = `${location.pathname}?${searchParams.toString()}`;
  history.push(newUrl);
};

/**
 * pasa de year month to calendarDate
 */
export const getFormattedDate = (calendarDateProp: string) => {
  const today = moment();
  const calendarDate = moment(calendarDateProp);

  const todayYearMonth = today.format('YYYY-MM');
  const calendarDateYearMonth = calendarDate.format('YYYY-MM');

  if (todayYearMonth === calendarDateYearMonth) {
    return today.format('YYYY-MM-DD');
  }

  return calendarDate.endOf('month').format('YYYY-MM-DD');
};

export const getFormattedDateForCalendar = (year: string | number, month: string | number) => {
  // The month is zero-based in the URL, so we need to add 1 to get the correct month
  const formattedMonth = Number(month) + 1;

  const yearMonth = `${year}-${formattedMonth}`;
  const today = moment();
  const date = moment(yearMonth, 'YYYY-MM');

  if (today.format('YYYY-MM') === date.format('YYYY-MM')) {
    return today.format('YYYY-MM-DD');
  }

  return date.endOf('month').format('YYYY-MM-DD');
};

export const getYearAndMonthQueryParams = () => {
  const year = getSearchParam(SEARCH_PARAM_KEYS.year) || moment().year();
  const month = getSearchParam(SEARCH_PARAM_KEYS.month) || moment().month();

  return { year, month };
};

export const closeModal = (
  history: History<unknown>,
  location: Location<unknown>,
  searchParamsObjToRedirect: Record<string, any>,
) => {
  const { pathname } = location;

  const queryString = getQueryString(searchParamsObjToRedirect);

  history.push(`${pathname}?${queryString}`);
};

export const closeModalReplace = (
  history: History<unknown>,
  location: Location<unknown>,
  searchParamsObjToRedirect: Record<string, any>,
) => {
  const { pathname } = location;

  const queryString = getQueryString(searchParamsObjToRedirect);

  history.replace(`${pathname}?${queryString}`);
};

export const getBranchForRedirectByRole = (user: SessionUser, queryParams: Record<TabName, string>) => {
  const isAdmin = getIsInAdminGroup(user);

  if (!isAdmin) {
    return user.branchId;
  }

  const branchParam = SEARCH_PARAM_KEYS.branch as TabName;
  return queryParams[branchParam];
};

/**
 * Create a function that given a string as a parameter removes spaces and - at the end of it.
 *
 * @param {String} str
 * @returns string
 */
export const removeSpacesAndDash = (str: string) => {
  if (str) {
    return str.replace(/[\s-]+$/, '');
  }
  return '';
};

export const getRepossessDate = (vehicle: any) =>
  vehicle?.rdnRepoDate || vehicle?.rdnRepossession?.rdnRepossessionDatetime;
export const getRepoAgentAvatarUrl = (vehicle: any) => vehicle?.repoAgentUser?.avatarUrl;
export const getRepoAgentNameOrRDN = (vehicle: any, roleName?: string) => {
  if (vehicle?.repoAgentUser?.firstName && vehicle?.repoAgentUser?.lastName) {
    return `${vehicle?.repoAgentUser?.firstName} ${vehicle?.repoAgentUser?.lastName}`;
  }

  if (vehicle?.repoAgentRdnId) {
    return `RDN ID: ${vehicle?.repoAgentRdnId}`;
  }

  return `Unknown ${roleName || 'User'}`;
};

export const getShiftNameOrBranch = (
  rdnCase: any,
  branches: SessionBranch[],
  plan: string | null,
  userField = 'spotter',
) => {
  const userBranchId = rdnCase?.[userField]?.branchId;
  const branchName = branches.find((branch) => branch.id === userBranchId)?.name || UNASSIGNED_BRANCH_NAME;

  if (plan === SubscriptionPlan.BASIC) return branchName;

  const shiftTime = rdnCase?.spotter?.shiftTimes;

  if (!shiftTime) {
    return branchName;
  }

  if (Array.isArray(shiftTime)) {
    return shiftTime[0]?.shift?.name || branchName;
  }

  return '';
};

/**
 * @deprecated use getShiftNameOrBranch instead.
 */
export const getShiftNameOfRepoAgent = (vehicle: any, branches: SessionBranch[]) => {
  const shiftTime = vehicle?.repoAgentUser?.shiftTimes;
  const userBranchId = vehicle?.repoAgentUser?.branchId;
  const branchName = branches.find((branch) => branch.id === userBranchId)?.name;

  if (!shiftTime) {
    return branchName;
  }

  if (Array.isArray(shiftTime)) {
    return shiftTime[0]?.shift?.name || branchName;
  }

  return '';
};

export const getSpottedVehicleDate = (vehicle: any) => vehicle?.spottedDate;
export const getSpotterAvatarUrl = (vehicle: any) => vehicle?.spotter?.avatarUrl;
export const getSpotterNameOrRDN = (vehicle: any) => {
  if (vehicle?.spotter?.firstName || vehicle?.spotter?.lastName) {
    return `${vehicle?.spotter?.firstName ?? ''} ${vehicle?.spotter?.lastName ?? ''}`;
  }

  if (vehicle?.spotter?.rdnId) {
    return `RDN ID: ${vehicle?.spotter?.rdnId}`;
  }

  return 'Unknown User';
};

/**
 * @deprecated use getShiftNameOrBranch instead.
 */
export const getShiftNameOfSpotter = (vehicle: any, branches: SessionBranch[]) => {
  const shiftTime = vehicle?.spotter?.shiftTimes;
  const userBranchId = vehicle?.spotter?.branchId;
  const branchName = branches.find((branch) => branch.id === userBranchId)?.name;

  if (!shiftTime) {
    return branchName;
  }

  if (Array.isArray(shiftTime)) {
    return shiftTime[0]?.shift?.name || branchName;
  }

  return '';
};

export const getYearMakeModel = (vehicle: any) => {
  const year = vehicle?.year_make_model || vehicle?.yearMakeModel;
  const vendorBranchName = vehicle?.vendor_branch_name || vehicle?.vendorBranchName;
  return `${year} ${vendorBranchName ? `- ${vendorBranchName}` : ''}`;
};

export const getDateRange = () => {
  const timeFormat = 'YYYY-MM-DD';
  const queryParams = getParamsObject();
  const isYearly = queryParams[SEARCH_PARAM_KEYS.ytd] === 'true';

  const { year, month } = getYearAndMonthQueryParams();
  const calendarDate = getFormattedDateForCalendar(year, month);

  const now = moment();

  let startDate;
  let endDate;

  if (isYearly) {
    startDate = moment(calendarDate).startOf('year').format(timeFormat);
  } else {
    startDate = moment(calendarDate).startOf('month').format(timeFormat);
  }

  if (isYearly && now.year() !== moment(calendarDate).year()) {
    endDate = moment()
      .set({ year: Number(year) })
      .endOf('year')
      .format(timeFormat);
  } else if (now.format('YYYY-MM') !== moment(calendarDate).format('YYYY-MM')) {
    endDate = moment(calendarDate).endOf('month').format(timeFormat);
  } else {
    endDate = moment().format(timeFormat);
  }

  return { startDate, endDate };
};

/**
 * Get object with branch visibility
 *
 * @param {boolean} hideCompanyWideManager - this is used in very specific cases like in reports (whole) and overview (live hits) when the manager can see company wide data
 */
export const getUserBranchVisibility = (user: SessionUser, hideCompanyWideManager = true) => {
  const branchObject = {
    hideUnassigned: false,
    hideCompanyWide: false,
  };

  const isAdmin = getIsInAdminGroup(user);

  if (isAdmin) {
    return branchObject;
  }

  branchObject.hideUnassigned = true;
  branchObject.hideCompanyWide = hideCompanyWideManager;
  return branchObject;
};

/**
 * checks for the 'from' query param exists and returns true if from = reports
 * @deprecated this is confusing, permissions should be handled per module and not per query param.
 */
export const checkIfCompanyWideTabForManagers = (queryParamsProp: Record<string, any>) => {
  const fromUrl = queryParamsProp?.from;
  const isFromReports = fromUrl?.split('/')?.includes('reports');

  if (isFromReports) {
    return false;
  }

  return true;
};

type TUser<T> = T & { user: Pick<User, 'firstName' | 'lastName'> };

export const searchByUser = <T>(array: Array<TUser<T>>, search: string | undefined): Array<TUser<T>> => {
  if (!search) return array;

  return array.filter((item) => {
    const name = `${item.user.firstName || ''} ${item.user.lastName || ''}`;
    return name.toLowerCase().includes(search.toLowerCase());
  });
};

export function joinClassNames(classNames: (string | null | undefined | false)[]) {
  const className = classNames.filter((item) => typeof item === 'string').join(' ');

  return className;
}

export function formatCaseDates({ timezone, date }: { timezone: string; date?: string | null }): string {
  return moment(date).isValid() ? moment(date).tz(timezone).format(CASE_DATE_TIME_FORMAT) : 'N/A';
}

export function downloadCSV(csvContent: string, fileName: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);

  // Trigger download
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Unauthorized/session-expiry sign-out for the legacy REST/apisauce client
 * (`services/clients/insightt-api-client.jsx`), the subscription RTK Query client and the Apollo
 * error link. Delegates to `modules/auth/sign-out.ts`'s `signOut`, the single owner that clears all
 * local state first and navigates exactly once (T066, T087).
 */
export function logoutSignOutAndCleanup() {
  // Unauthorized cleanup has one owner (T066/T087): full local cleanup first, then credential and
  // provider termination, then exactly one navigation — see `modules/auth/sign-out.ts`.
  void signOut();
}

export function validateUnitsPendingSortKey(genericString?: string): UnitPendingSortKey | undefined {
  if (
    genericString === UnitPendingSortKey.LENDER_CLIENT_NAME ||
    genericString === UnitPendingSortKey.YEAR_MAKE_MODEL ||
    genericString === UnitPendingSortKey.SPOTTED_DATE
  )
    return genericString;

  return undefined;
}

export function formatAddressFromMicroService(
  location: ValidatedLocationFromAddressQuery['validatedLocationFromAddress'],
): AddressType {
  const { zipCode, city, state, latitude, longitude, address1, address2, country, county } = location;

  const formattedAddress = `${address1 || ''} ${address2 || ''} ${city || ''} ${state || ''} ${zipCode} ${
    country || ''
  } ${county || ''}`;

  const formattedCoordToString = `${latitude || ''}, ${longitude || ''}`;

  return {
    formattedAddress: address1 ? formattedAddress.trim() : formattedCoordToString,
    lat: latitude || 0,
    lng: longitude || 0,
    zipCode: zipCode,
    city,
    state,
    county,
  };
}

export const base64ToBlob = (base64: string, mimeType = 'audio/mp3'): Blob => {
  const cleanBase64 = base64.replace(/^data:.*?;base64,/, '');

  const byteCharacters = atob(cleanBase64);

  const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);

  return new Blob([byteArray], { type: mimeType });
};

/**
 * @deprecated since 2026-07-31. use `CaseAddressDAO` instead.
 */
export const formatCaseAddress = (caseAddress: Partial<CaseAddress>): string | undefined => {
  //TODO delete use oldFullAddress
  const { latitude, longitude, city, state, address, fullAddress: oldFullAddress } = caseAddress;

  const fullAddress = address && city && state ? toFullAddress(caseAddress) : oldFullAddress;

  return fullAddress || (latitude && longitude ? `${latitude}, ${longitude}` : undefined);
};

const isNullUndefinedOrEmpty = (value: string | null | undefined): boolean => {
  if (value === null || value === undefined) return true;
  return value.trim() === '';
};

/**
 * @deprecated since 2026-07-31. use `CaseAddressDAO` instead.
 */
export const toFullAddress = (address: Partial<CaseAddress>, skip: string[] = []): string => {
  const fullAddress = [];
  if (!isNullUndefinedOrEmpty(address.address)) {
    fullAddress.push(address.address);
  }
  if (!isNullUndefinedOrEmpty(address.city) && !skip.includes('city')) {
    fullAddress.push(address.city);
  }
  if (!isNullUndefinedOrEmpty(address.state) && !skip.includes('state')) {
    fullAddress.push(address.state);
  }
  if (!isNullUndefinedOrEmpty(address.zip) && !skip.includes('zip')) {
    fullAddress.push(address.zip);
  }

  return fullAddress.join(', ');
};

export function formatToCapitalizeWords(value: string): string {
  if (!value) return value;

  return (
    value
      // spottedVehicle -> spotted Vehicle
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // spotted_vehicle / spotted-vehicle -> spotted Vehicle
      .replace(/[_-]+/g, ' ')
      // remove extra spaces
      .trim()
      // split into words, capitalize each one
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  );
}

export function getOwnProps<T extends object>(obj: T): Record<string, any> | T {
  const result: Record<string, any> = {};
  try {
    Object.getOwnPropertyNames(obj).forEach((prop) => {
      result[prop] = (obj as any)[prop];
    });
  } catch {
    return obj;
  }
  return result;
}

export const convertImageCarousel = ({
  slides,
  dateFeatureFlag,
}: {
  slides: ImageOutputData[];
  dateFeatureFlag: boolean;
}): CarouselSlide[] => {
  return slides.map((slide) => ({
    image: slide.url,
    uploadDate: slide.date && dateFeatureFlag ? DateTime.fromISO(slide.date) : undefined,
  }));
};

/**
 * Converts a Luxon DateTime object to a specific timezone.
 * @param dateTime The Luxon DateTime object to convert.
 * @param timeZone The target timezone.
 * @param keepLocalTime If true, keeps the wall time; if false, converts the instant.
 * @returns A new DateTime object with the specified timezone.
 */
export const convertDateTimeToTimeZone = (
  dateTime: DateTime,
  timeZone: string,
  keepLocalTime: boolean = false,
): DateTime => {
  return dateTime.setZone(timeZone, { keepLocalTime });
};

export const resolveCoordinatesByPriority = ({
  status,
  rdnRepossession,
  spottedVehicleReports,
  caseAddresses,
}: ResolveCoordinatesByPriorityParams): ResolveCoordinatesByPriorityResult | null => {
  const repoAddress = rdnRepossession?.caseAddress;
  const spottedAddress = spottedVehicleReports?.[0]?.caseAddress;
  const caseAddress = caseAddresses?.[0];

  const isRepossessed = status === CaseStatusRDNMatch.REPOSSESSED || status === CaseStatus.REPOSSESSED;

  if (isRepossessed && repoAddress?.latitude != null && repoAddress?.longitude != null) {
    return { lat: repoAddress.latitude, lng: repoAddress.longitude };
  }

  if (spottedAddress?.latitude != null && spottedAddress?.longitude != null) {
    return { lat: spottedAddress.latitude, lng: spottedAddress.longitude };
  }

  if (caseAddress?.latitude != null && caseAddress?.longitude != null) {
    return { lat: caseAddress.latitude, lng: caseAddress.longitude };
  }

  return null;
};

/**
 * @deprecated since 2026-07-31. use `CaseAddressDAO` instead.
 */
export const formatAddressFromCaseAddress = (
  caseAddress: Partial<CaseAddress>,
  isRepossessed: boolean,
): string | null => {
  const street =
    isRepossessed && caseAddress.originalAddress?.trim() ? caseAddress.originalAddress : caseAddress.address;
  const city = caseAddress.city;
  const state = caseAddress.state;
  const zip = caseAddress.zipCodeFirst5;

  if (!street?.trim() || !city?.trim() || !state?.trim() || !zip?.trim()) {
    const { latitude, longitude } = caseAddress;
    const isValidLat = typeof latitude === 'number' && Number.isFinite(latitude);
    const isValidLon = typeof longitude === 'number' && Number.isFinite(longitude);

    return isValidLat && isValidLon ? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}` : null;
  }

  const cleanStreet = street.trim();
  const finalParts = [cleanStreet];

  if (!cleanStreet.includes(city.trim())) finalParts.push(city.trim());
  if (!cleanStreet.includes(state.trim())) finalParts.push(state.trim());
  if (!cleanStreet.includes(zip.trim())) finalParts.push(zip.trim());
  return finalParts.join(', ');
};

export const resolveAddressByPriority = ({
  status,
  rdnRepossession,
  spottedVehicleReports,
  caseAddresses,
  showGivenAddress,
}: ResolveAddressByPriorityParams): ResolveAddressByPriorityResult | null => {
  const repoAddress = rdnRepossession?.caseAddress;
  const repoAddressDAO = repoAddress ? createCaseAddressDAO(repoAddress) : null;
  const formattedRepoAddress = repoAddressDAO?.formattedOriginalAddress || repoAddressDAO?.coordinatesAddress;
  const spottedAddress = spottedVehicleReports?.[0]?.caseAddress;
  const givenAddress = caseAddresses?.[0];

  const isRepossessed = status === CaseStatusRDNMatch.REPOSSESSED || status === CaseStatus.REPOSSESSED;

  if (isRepossessed && formattedRepoAddress) {
    return { resolvedAddress: formattedRepoAddress, isGivenAddress: false };
  }

  if (spottedAddress) {
    const spotAddressDAO = createCaseAddressDAO(spottedAddress);
    const formattedSpottedAddress = spotAddressDAO.formattedAddress || spotAddressDAO.coordinatesAddress;
    return { resolvedAddress: formattedSpottedAddress, isGivenAddress: false };
  }

  if (showGivenAddress && givenAddress) {
    const spotAddressDAO = createCaseAddressDAO(givenAddress);
    const formattedGivenAddress = spotAddressDAO.formattedOriginalAddress || spotAddressDAO.coordinatesAddress;
    return { resolvedAddress: formattedGivenAddress, isGivenAddress: true };
  }

  return null;
};
